import {doc} from 'firebase/firestore';
import {useMemo} from 'react';
import {useFirestore, useFirestoreDoc, useFirestoreDocData} from 'reactfire';
import {useAuthUser} from '../../auth/hooks/useAuthUser';
import {EntityConverter} from '../EntityConverter';
import {FirestoreDocument} from '../FirestoreDocument';
import {Document} from '../types';
import {Entity, EntityWithMetadata, IdField} from '#common/models/Entity';

export function useDocument<T extends Entity>(path: string, ...segments: string[]): Document<T> {
  const {data: currentUser} = useAuthUser();
  const firestore = useFirestore();

  // Use a stable JSON representation of segments for useMemo dependency
  const segmentsKey = JSON.stringify(segments);

  const reference = useMemo(() => {
    const actualSegments = JSON.parse(segmentsKey) as string[];
    const document = doc(firestore, path, ...actualSegments);
    return document.withConverter(new EntityConverter<EntityWithMetadata<T>>());
  }, [firestore, path, segmentsKey]);

  const rawSnapshot = useFirestoreDoc<EntityWithMetadata<T>>(reference, {suspense: false});
  const rawData = useFirestoreDocData<EntityWithMetadata<T>>(reference, {idField: IdField, suspense: false});

  const snapshot = useMemo(
    () => ({
      data: rawSnapshot.data,
      error: rawSnapshot.error,
      firstValuePromise: rawSnapshot.firstValuePromise,
      hasEmitted: rawSnapshot.hasEmitted,
      isComplete: rawSnapshot.isComplete,
      status: rawSnapshot.status,
    }),
    [
      rawSnapshot.data,
      rawSnapshot.error,
      rawSnapshot.firstValuePromise,
      rawSnapshot.hasEmitted,
      rawSnapshot.isComplete,
      rawSnapshot.status,
    ],
  );

  const data = useMemo(
    () => ({
      data: rawData.data,
      error: rawData.error,
      firstValuePromise: rawData.firstValuePromise,
      hasEmitted: rawData.hasEmitted,
      isComplete: rawData.isComplete,
      status: rawData.status,
    }),
    [rawData.data, rawData.error, rawData.firstValuePromise, rawData.hasEmitted, rawData.isComplete, rawData.status],
  );

  return useMemo(() => {
    const currentUserId = currentUser?.uid ?? '';
    return new FirestoreDocument<T>(firestore, currentUserId, snapshot, data);
  }, [currentUser?.uid, data, firestore, snapshot]);
}
